import { useCallback } from 'react';

// Declare the plausible function on the window object
declare global {
    interface Window {
        plausible?: (eventName: string, options?: {
            callback?: () => void;
            props?: Record<string, string | number>;
            revenue?: Record<string, number>;
            interactive?: boolean;
        }) => void;
    }
}

interface PlausibleEventOptions {
    callback?: () => void;
    props?: Record<string, string | number>;
    revenue?: Record<string, number>;
    interactive?: boolean;
}

export const usePlausible = () => {
    const trackEvent = useCallback((eventName: string, options?: PlausibleEventOptions) => {
        // Check if plausible is available (script loaded)
        if (typeof window !== 'undefined' && window.plausible) {
            try {
                window.plausible(eventName, options);
                console.log(`Plausible event tracked: ${eventName}`, options);
            } catch (error) {
                console.error('Error tracking Plausible event:', error);
            }
        } else {
            console.warn('Plausible not loaded, event not tracked:', eventName);
        }
    }, []);

    // Convenience methods for common events
    const trackNamePicked = useCallback((name: string) => {
        trackEvent('Name Picked', {
            props: { name: name.substring(0, 50) } // Limit name length for privacy
        });
    }, [trackEvent]);

    const trackMemeSelected = useCallback((memeId: string, category?: string, isCustom?: boolean) => {
        trackEvent('Meme Selected', {
            props: {
                meme_id: memeId,
                category: category || 'unknown',
                is_custom: isCustom ? 'true' : 'false'
            }
        });
    }, [trackEvent]);

    const trackTextAdded = useCallback((textLength: number) => {
        trackEvent('Text Added', {
            props: { text_length: textLength }
        });
    }, [trackEvent]);

    const trackStickerAdded = useCallback((stickerName: string) => {
        trackEvent('Sticker Added', {
            props: { sticker_name: stickerName }
        });
    }, [trackEvent]);

    const trackMemeSubmitted = useCallback((elementsCount: number, hasText: boolean, hasStickers: boolean) => {
        trackEvent('Meme Submitted', {
            props: {
                elements_count: elementsCount,
                has_text: hasText ? 'true' : 'false',
                has_stickers: hasStickers ? 'true' : 'false'
            }
        });
    }, [trackEvent]);

    return {
        trackEvent,
        trackNamePicked,
        trackMemeSelected,
        trackTextAdded,
        trackStickerAdded,
        trackMemeSubmitted,
    };
};
